// Layer 4 (UI): the Period close tab body (MTD only). polish.P9 (#657): it was
// a collapsed summary line at the foot of the page; it is now a tab, open by
// definition, so the disclosure and its "N rows" summary go. The June table
// reads first, then May's closed table. Renders `periodClosePanel` verbatim —
// the engine's figures as they are.
//
// K17 round 1 (#663): the closed May table's third column is "Unaccounted
// loss"; the open June table's is "Not yet despatched" (concentrate produced
// but not shipped), unsigned and untoned. The May caption carries the
// estimate's measured-window coverage as provenance, derived from the estimate
// function's own return.
//
// K17 round 2 (#663): the June table's caption shows its own window —
// `1–7 Jun · 7 measured days` — also derived from the estimate's coverage. The
// June close closes D25's window unscaled, so the caption and the figure name
// the same 7 days; a lot dated 8 Jun is outside it and the window says so.
//
// K17 round 3 (#663) item 2: the closed May table shows its coverage inline
// too — `25–31 May · 7 measured days scaled to 31` — so the two tables state
// their windows the same way; before, May's coverage lived only in the aside
// chip's tooltip and a hover was needed to read it.

import type { ReactNode } from 'react';
import { Pill, Table, TBody, TD, TH, THead, TR } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import { CARD_TABLE } from '@/versions/v1/modules/downtime/DowntimeCard';
import type { PeriodView } from '@/versions/v1/modules/ingestion/MetalAccountingLayer.transform';
import { periodClosePanel } from '@/versions/v1/modules/ingestion/MetalAccountingLayer.transform';
import { STAGE_LABEL_CAPS } from '@/versions/v1/modules/advisory/stage-body-ui';
import { STATE_PILL } from './BalanceStrip';

type CloseRows = NonNullable<ReturnType<typeof periodClosePanel>>['rows'];

export function PeriodClosePanel({ view }: { view: PeriodView }) {
  const panel = periodClosePanel(view);
  if (!panel) return null;

  const mayRows = panel.rows.filter((row) => row.month === 'may-2026');
  const junRows = panel.rows.filter((row) => row.month === 'june-2026-mtd');
  const mayProvenance = mayRows[0]?.estimateProvenance;
  // K17 round 2 (#663) item 1: the June table's caption shows the window its
  // estimate and its lots cover — `1–7 Jun · 7 measured days`, derived from the
  // coverage the estimate returns. The window is the statement; no sentence.
  const junWindow = junRows[0]?.estimateProvenance;

  return (
    <Commentable name="PeriodClose">
      <div className="space-y-4" data-period-close>
        <CloseTable
          caption="June 2026 — to date"
          windowCaption={junWindow}
          rows={junRows}
          open
          aside={<ProvenanceChip text="[syn]" title={panel.provenance} full />}
        />
        <CloseTable
          caption="May 2026 — closed"
          windowCaption={mayProvenance}
          rows={mayRows}
          aside={mayProvenance ? <ProvenanceChip text="[syn]" title={mayProvenance} full /> : undefined}
        />
      </div>
    </Commentable>
  );
}

function CloseTable({
  caption,
  windowCaption,
  rows,
  aside,
  open = false,
}: {
  caption: string;
  /** The close's measured window, derived from the estimate's coverage (K17 round 2). */
  windowCaption?: string;
  rows: CloseRows;
  aside?: ReactNode;
  /** Open month: the third column is "Not yet despatched", not "Unaccounted loss". */
  open?: boolean;
}) {
  if (rows.length === 0) return null;
  return (
    <div className="space-y-1">
      <p className="flex items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          <span className={STAGE_LABEL_CAPS}>{caption}</span>
          {windowCaption ? (
            <span className="text-xs text-muted" data-close-window={windowCaption}>
              {windowCaption}
            </span>
          ) : null}
        </span>
        {aside}
      </p>
      <div className={CARD_TABLE}>
        {/* Fixed columns: the June and May tables line up under each other. */}
        <Table wrap={false} className="table-fixed tabular-nums">
          <THead>
            <TR>
              <TH className="w-[10%]">Metal</TH>
              <TH className="w-[18%] text-right">Despatched</TH>
              <TH className="w-[18%] text-right">Estimate</TH>
              <TH className="w-[24%] text-right">{open ? 'Not yet despatched' : 'Unaccounted loss'}</TH>
              <TH className="w-[30%] pl-6">State</TH>
            </TR>
          </THead>
          <TBody>
            {rows.map((row) => (
              <TR key={row.id} data-close-row={row.id}>
                <TD className="whitespace-nowrap font-medium !text-ink-primary">{row.element}</TD>
                <TD className="whitespace-nowrap text-right">{row.despatchedContained}</TD>
                <TD className="whitespace-nowrap text-right" title={row.estimateProvenance}>
                  {row.estimateContained}
                </TD>
                <TD className="whitespace-nowrap text-right">{row.unaccountedLoss}</TD>
                <TD className="whitespace-nowrap pl-6">
                  <Pill tone={row.state === 'closed' ? 'success' : 'default'} className={STATE_PILL}>
                    {row.state === 'closed' ? 'closed' : 'open'}
                  </Pill>
                  {row.competentPerson ? (
                    <span className="ml-1.5 text-xs text-muted" title={row.closedAt ?? undefined}>
                      {row.competentPerson}
                      {row.closedAt ? ` · ${row.closedAt}` : ''}
                    </span>
                  ) : null}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </div>
  );
}
